<template>
  <div class="container">
    <el-backtop target="body"></el-backtop>
    <div class="confirmation-wrapper" v-if="allMemberships && allMemberships.length">
      <h2 class="section-header">Registrations</h2>
      <div v-for="(membership, index) in allMemberships" :key="index" class="registration-card">
        <h2 class="event-name">
          <el-tag type="info" size="medium">
            {{ getProductType(membership) }}
          </el-tag>
          {{ getProductName(membership) }}
        </h2>

        <div class="event-details">
          <div class="detail-item" v-if="membership.startDate">
            <i class="el-icon-date"></i>
            <span>Start: {{ membership.startDate | dateFormatter }}</span>
          </div>
          <div class="detail-item" v-if="membership.endDate">
            <i class="el-icon-date"></i>
            <span>End: {{ membership.endDate | dateFormatter }}</span>
          </div>
          <div class="detail-item">
            <i class="el-icon-tickets"></i>
            <span>Product Type: {{ membership.productType | productTypeFormatter }}</span>
          </div>
          <div class="detail-item" v-if="membership.team">
            <i class="el-icon-user"></i>
            <span>Team Name: {{ membership.team.name }}</span>
          </div>

        </div>

        <add-to-calendar
          v-if="membership.entityType === 'event' || membership.team"
          :title="membership.entityName"
          :location="membership.location || ''"
          :start="toDateObject(membership.startDate)"
          :end="toDateObject(membership.endDate)"
          inline-template>
          <div class="calendar-link-wrapper">
            <google-calendar id="google-calendar" class="calendar-link">
              <i class="el-icon-date"></i> Add to Google calendar
            </google-calendar>
          </div>
        </add-to-calendar>

        <div class="pricing">
          <span class="price-label">Product Price:</span>
          <span class="price-value">{{ membership.product.price | dollarFormatter }}</span>
        </div>

        <!-- Family members section for family memberships -->
        <div class="family-members" v-if="membership.isFamilyMembership">
    <h3>Family Members</h3>
    <div class="family-members-grid">
      <div v-for="(member, memberIndex) in membership.familyRegistrations" :key="memberIndex" class="family-member">
        <div class="member-info">
          <div class="member-name">
            {{ get(member, 'profile.firstName', '') }} {{ get(member, 'profile.lastName', '') }}
          </div>
          <div class="member-details">
            <span>DOB: {{ get(member, 'profile.dob', '') | dateFormatter }}</span>
            <span>Gender: {{ get(member, 'profile.gender', '') | capitalize }}</span>
          </div>
        </div>
        <el-tag size="small" type="info">
          {{ member.memberType }}
        </el-tag>
      </div>
    </div>
  </div>

        <!-- Saleable Items -->
        <div v-if="filteredSaleableItems(membership)?.length" class="saleable-items">
  <h4>Items</h4>
  <div v-for="item in filteredSaleableItems(membership)" :key="item.id" class="saleable-item pr-0">
    <div class="item-content">
      <div class="item-image">
        <img :src="getImage(item)" :alt="item.name" />
      </div>
      <div class="item-details">
        <h3 class="item-name">{{ item.name }}</h3>
        <div class="item-info">
          <span class="quantity">
            <i class="el-icon-shopping-cart-full"></i>
            Qty: {{ item.qty }}
          </span>
          <span class="price">
            <i class="el-icon-money"></i>
            {{ item.price | dollarFormatter }}
          </span>
        </div>
      </div>
      <div class="item-total">
        <span class="total-label">Sub Total:</span>
        <span class="total-value">{{ (item.price * item.qty) | dollarFormatter }}</span>
      </div>
    </div>
  </div>
</div>

        <!-- Team Code Display -->
        <div v-if="membership && membership.teamCode && hasProductRequiringTeamCode(membership)" class="team-code-section">
          <div class="team-code-compact">
            <strong>Team Code:</strong>
            <span class="code-value">{{ membership.teamCode }}</span>
            <el-button
              type="text"
              size="mini"
              v-clipboard="membership.teamCode"
              @click="$message.success('Team code copied to clipboard')">
              <i class="el-icon-copy-document"></i> Copy
            </el-button>
          </div>
          <div class="team-code-note">
            <small><i class="el-icon-info"></i> Share this code with team members for direct registration</small>
          </div>
        </div>

        <!-- Team Products -->
        <div v-if="membership.regoLinks && membership.regoLinks.length" class="team-products">
          <h3>Team Products</h3>
          <div v-for="p in filteredProducts(membership)" :key="p._id" class="team-product">
            <div class="team-product-details">
              <div class="team-product-tags">
                <el-tag size="mini" v-if="p.productDetails.memberType">
                  {{ p.productDetails.memberType | capitalize }}
                </el-tag>
                <el-tag size="mini" v-if="p.productDetails.gender && p.productDetails.gender !== 'undefined'">
                  {{ p.productDetails.gender === 'All' ? 'All genders' : p.productDetails.gender | capitalize }}
                </el-tag>
                <el-tag size="mini">
                  Age {{ p.productDetails.ageFrom }} {{ p.productDetails.ageTo !== 99 ? `- ${p.productDetails.ageTo}` : '+' }}
                </el-tag>
              </div>
              <span class="team-product-name">{{ p.productDetails.name }}</span>
              <span class="team-product-price">{{ priceFormatted(p.productDetails.price) }}</span>
            </div>
            <div class="team-product-actions">
              <el-button-group>
                <el-button type="secondary" size="mini" v-clipboard="p.link" @click="$message.success('Successfully copied to clipboard')">
                  Copy
                </el-button>
                <el-button type="info" size="mini" @click="inviteMembers(p, membership)" v-if="membership.teamMembers && membership.teamMembers.length > 0">
                  Invite
                </el-button>
                <el-popover
                  placement="bottom"
                  width="400"
                  trigger="click">
                  <el-alert
                    type="info"
                    :closable="false"
                    show-icon>
                    <span class="f-bold break-word">Only members who are eligible to join the team will be displayed</span>
                  </el-alert>
                  <el-table
                    :data="filteredMemberList(p)"
                    max-height="420"
                    @row-click="handleMemberSelected(p, $event, membership)">
                    <el-table-column width="80" property="_id" label="ID">
                      <template slot-scope="scope">
                        <span>{{ scope.row._id }}</span>
                      </template>
                    </el-table-column>
                    <el-table-column min-width="120" label="Member Name">
                      <template slot-scope="scope">
                        <span class="break-word">{{ `${scope.row.profile.firstName} ${scope.row.profile.lastName}` }}</span>
                      </template>
                    </el-table-column>
                    <el-table-column width="120" label="Date of Birth">
                      <template slot-scope="scope">
                        <div style="display: flex; align-items: flex-start;">
                          <span class="break-word">{{ scope.row.profile.dob }}</span>
                        </div>
                      </template>
                    </el-table-column>
                  </el-table>
                  <el-button slot="reference" type="primary" size="mini">
                    <i class="el-icon-edit"></i>
                    Register
                    <i class="el-icon-arrow-down el-icon--right"></i>
                  </el-button>
                </el-popover>
              </el-button-group>
            </div>
          </div>
        </div>

        <div class="subtotal">
          <span class="subtotal-label">Sub Total:</span>
          <span class="subtotal-value">{{ calculateTotal(membership) | dollarFormatter }}</span>
        </div>
      </div>
    </div>

    <div class="confirmation-wrapper" v-if="saleableItemConfirmation && saleableItemConfirmation.length">
      <h2 class="section-header">Store Items</h2>
    <div v-for="(item, index) in saleableItemConfirmation" :key="index" class="saleable-item">
      <div class="item-content">
        <div class="item-image">
          <img :src="getImage(item)" :alt="item.name" />
        </div>
        <div class="item-details">
          <h3 class="item-name">{{ item.name }}</h3>
          <div class="item-info">
            <span class="quantity">
              <i class="el-icon-shopping-cart-full"></i>
              Qty: {{ item.qty }}
            </span>
            <span class="price">
              <i class="el-icon-money"></i>
              {{ item.price | dollarFormatter }}
            </span>
          </div>
        </div>
        <div class="item-total">
          <span class="total-label">Sub Total:</span>
          <span class="total-value">{{ (item.price * item.qty) | dollarFormatter }}</span>
        </div>
      </div>
    </div>
  </div>

  <div v-if="totalWithSaleableItems >= 0" class="confirmation-wrapper total-amount">
  <div class="registration-card">
    <div class="subtotal discount">
      <span class="subtotal-label">Total Discount:</span>
      <span class="subtotal-value discount-value">{{ totalDiscount | discountFormatter }}</span>
    </div>
    <div class="subtotal">
  <div class="subtotal-label">
    Total Amount Paid:
    <br>
    <span class="subtotal-label-small">(Including Fees)</span>
  </div>

  <span class="subtotal-value">{{ totalWithSaleableItems | dollarFormatter }}</span>
</div>
  </div>
</div>
    <el-dialog  title="Invite Members" :visible.sync="showInviteModal">
      <!-- description, the list below is the list of members has registered or added to this team in the previous event, send them an join invitaion via email, display in  -->
      <div class="description">
        <el-alert
          class="mb-15 break-word"
          title="Select from the list of members who have previously registered to this team to send an email invitation to register to this event."
          type="info"
          :closable="false"
          show-icon>
          <!-- <p>The list below is the list of members has registered or added to this team in the previous event, send them an join invitaion via email.</p> -->
        </el-alert>
      </div>

      <!-- Team Code Option -->
      <div class="team-code-option" v-if="inviteProduct && inviteProduct.teamCode">
        <el-checkbox v-model="includeTeamCode">
          <strong>Include team code in invitation email</strong>
        </el-checkbox>
        <div class="team-code-info" v-if="includeTeamCode">
          <el-alert
            type="success"
            :closable="false"
            show-icon
            class="mt-10">
            <div slot="title">Team Code: <strong>{{ inviteProduct.teamCode }}</strong></div>
            <p>This code will be included in the invitation email so members can use it during registration to join the team directly.</p>
          </el-alert>
        </div>
      </div>

      <div class="member-selection">
        <el-checkbox :indeterminate="isIndeterminate" v-model="checkAll" @change="handleCheckAllChange">Select all</el-checkbox>
      </div>
      <div class="member-selection" v-if="members.length">
        <el-checkbox-group v-model="selectedMembers" @change="handleSelectMembers">
          <div class="member-item" v-for="member in members" :key="member.id">
          <el-checkbox :label="member._id">
            {{ get(member, 'profile.firstName') }} {{ get(member, 'profile.lastName') }}
            </el-checkbox>
          </div>
        </el-checkbox-group>
      </div>
      <span slot="footer" class="dialog-footer">        <el-button @click="showInviteModal = false">Cancel</el-button>
        <el-button type="primary" @click="inviteSelectedMembers">Send Invite <i class="el-icon-s-promotion"></i></el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { calculateMemberAge } from '@/utils/helpers';
import moment from 'moment';
import _ from 'lodash';

export default {
  name: 'SuccessConfirmation',
  props: {
    // confirmation: {
    //   type: Array,
    //   required: true,
    // },
    saleableItemConfirmation: {
      type: Array,
      required: false,
    },
    isRegoBlurred: {
      type: Boolean,
      default: true
    },
    isSIBlurred: {
      type: Boolean,
      default: true
    },
    regoErrorMessages: {
      type: String,
      default: ''
    },
    productError: {
      type: Object,
      default: () => {}
    },
    memberIdError: {
      type: Number,
      default: undefined
    },
    siErrorMessages: {
      type: String,
      default: ''
    },
    bulkMembers: {
      type: Array,
      default: () => []
    },
    memberList: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      get: _.get,
      capitalize: _.capitalize,
      moment,
      isRegoBlurredFlag: true,
      isSIBlurredFlag: true,
      regoErrorMessagesText: '',
      siErrorMessagesText: '',
      windowWidth: window.innerWidth,
      showInviteModal: false,
      members: [],
      selectedMembers: [],
      checkAll: false,
      isIndeterminate: false,
      selectedMember: undefined,
      defaultImage: process.env.VUE_APP_LOGO,
      localCurrentConfirmation: [],
      includeTeamCode: false,
    };
  },
  created() {
    this.isRegoBlurredFlag = this.isRegoBlurred;
    this.isSIBlurredFlag = this.isSIBlurred;
    this.regoErrorMessagesText = this.regoErrorMessages;
    this.siErrorMessagesText = this.siErrorMessages;
    window.addEventListener('resize', this.handleResize);

    // this.updateCurrentConfirmation();
  },
  filters: {
    captalize(value) {
      return _.capitalize(value);
    },
    dateFormatter(value) {
      return moment(value).format('DD/MM/YYYY');
    },
    timeFormatter(value) {
      return moment(value).format('HH:mm');
    },
    dollarFormatter(value) {
      return `$${(value / 100).toFixed(2)}`;
    },
    discountFormatter(value) {
      const absValue = Math.abs(value / 100).toFixed(2);
      return `$${absValue}`;
    },
    capitalize(value) {
      if (!value) return '';
      // eslint-disable-next-line no-param-reassign
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    productTypeFormatter(value) {
      const productMapping = [
        { code: 'indv', name: 'Individual' },
        { code: 'team', name: 'Team' },
        { code: 'indv-to-team', name: 'Individual to Team' },
        { code: 'entity', name: 'Entity' },
        { code: 'family', name: 'Family' },
      ];
      const product = productMapping.find((p) => p.code === value);
      return product ? product.name : '';
    }
  },
  computed: {
    ...mapGetters('cart', ['selectedBulkMembers', 'currentConfirmation', 'totalWithSaleableItems', 'totalDiscount']),
    isTablet() {
      return this.windowWidth <= 768;
    },
    currentConfirmation() {
      return this.$store.getters['cart/currentConfirmation'];
    },
    nonFamilyMemberships() {
      return this.currentConfirmation.filter((item) => item.productType !== 'family');
    },
    familyMemberships() {
      return this.currentConfirmation.filter((item) => item.productType === 'family');
    },
    allMemberships() {
      return [
        ...this.nonFamilyMemberships,
        ...this.familyMemberships.map((family) => ({
          ...family,
          isFamilyMembership: true
        }))
      ];
    }
  },
  methods: {
    filteredProducts(event) {
      console.log('events', event);
      return event.regoLinks.filter((l) => l.productDetails.ageFrom === event.product.ageFrom && l.productDetails.ageTo === event.product.ageTo && (!event.product.gender || event.product.gender === 'All' || event.product.gender === l.productDetails.gender));
    },
    getProductType(membership) {
      if (membership.team) return 'Event';
      if (membership.entityType) return _.capitalize(membership.entityType);
      return membership.isFamilyMembership ? 'Family' : 'Event';
    },
    getProductName(membership) {
      return membership.entityName || membership.name || 'Product';
    },
    filteredSaleableItems(membership) {
      return membership.saleableItems?.filter((item) => item.qty > 0) || [];
    },
    calculateTotal(membership) {
      const membershipPrice = membership.product.price;
      const saleableItemsTotal = this.filteredSaleableItems(membership)
        .reduce((sum, item) => sum + item.price * item.qty, 0);
      return membershipPrice + saleableItemsTotal;
    },
    hasProductRequiringTeamCode(membership) {
      console.log('Checking hasProductRequiringTeamCode for:', membership);
      // Check if any product in regoLinks requires team code
      if (membership.regoLinks && membership.regoLinks.length) {
        console.log('membership.regoLinks', membership.regoLinks);
        const hasRequiredTeamCode = membership.regoLinks.some((link) => {
          console.log('Checking link:', link.productDetails);
          return link.productDetails && link.productDetails.requireTeamCode === true;
        });
        console.log('regoLinks requireTeamCode result:', hasRequiredTeamCode);
        return hasRequiredTeamCode;
      }
      // Also check the main product
      const mainProductRequires = membership.product && membership.product.requireTeamCode === true;
      console.log('Main product requireTeamCode:', mainProductRequires);
      return mainProductRequires;
    },

    filteredMemberList(product) {
      console.log('product', product);
      console.log('memberList', this.memberList);
      return this.memberList.filter((member) => this.isEligible(member, product));
    },
    handleMemberSelected(product, member, rego) {
      console.log('product', product);
      console.log('member', member);
      console.log('rego', rego);
      this.selectedMember = member;

      // Emit the event to update the selected member
      this.$emit('selectedMember', { member, product, rego });
    },
    calculateAge(dob) {
      const endOfYear = this.$store.getters['national/endOfYear'];
      const age = calculateMemberAge(dob, endOfYear);
      return age;
    },
    isEligible(member, product) {
      if (member) {
        // find the registration product based on the productid
        // const foundProduct = this.localRegistration.product.find((p) => p._id === productId);
        if (product) {
          // check gender of the product  and calculate the age of the member and conpare with the age range of the product
          const {
            gender, ageFrom, ageTo,
          } = product.productDetails;
          // console.log('product', product);
          const { dob, gender: memberGender } = member.profile;
          // console.log('dob', dob);
          // console.log('memberGender', memberGender);
          const age = this.calculateAge(dob);
          // console.log('age', age);
          if (ageFrom <= age && ageTo >= age && (memberGender === gender || gender === 'All' || !gender || (gender === 'Other' && (memberGender !== 'Male' && memberGender !== 'Female')))) {
            return true;
          }
          return false;
        }
      }
      return true;
    },
    getImage(item) {
      const hasImage = item.images && item.images.length > 0;
      const url = hasImage ? item.images[0].url : this.defaultImage;
      return url || this.defaultImage;
    },
    toDateObject(d) {
      return moment(d).toDate();
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    priceFormatted(price) {
      return `$${parseInt(price / 100).toFixed(2)}`;
    },
    inviteMembers(p, slot) {
      console.log('p', p);
      console.log('slot', slot);
      console.log('slot.team', slot.team);
      console.log('slot.team.passcode', slot.team && slot.team.passcode);
      console.log('hasProductRequiringTeamCode(slot)', this.hasProductRequiringTeamCode(slot));
      this.showInviteModal = true;
      this.members = slot.teamMembers;
      this.link = p.link;
      this.inviteProduct = slot;
    },
    handleCheckAllChange(val) {
      // this.selectAll = !this.selectAll;
      this.selectedMembers = val ? this.members.map((m) => m._id) : [];
      this.isIndeterminate = false;
    },
    async inviteSelectedMembers() {
      console.log('selectedMembers', this.selectedMembers);
      // send email to selectedMembers
      this.$store.commit('loading', true);
      await this.$http.post('assemble/api/v1/portal/members/invite', {
        members: this.selectedMembers,
        product: this.inviteProduct,
        link: this.link,
        includeTeamCode: this.includeTeamCode,
        teamCode: this.inviteProduct.teamCode
      });
      this.$store.commit('loading', false);

      this.showInviteModal = false;
      this.selectedMembers = [];
      this.members = [];
      this.link = '';
      this.inviteProduct = {};
      this.includeTeamCode = false;
      this.checkAll = false;
      this.isIndeterminate = false;
      this.$message.success('Invitation sent to selected members');
    },
    handleSelectMembers(value) {
      const checkedCount = value.length;
      this.checkAll = checkedCount === this.members.length;
      this.isIndeterminate = checkedCount > 0 && checkedCount < this.members.length;
    },
  },
  watch: {
    isRegoBlurred(val) {
      this.isRegoBlurredFlag = val;
    },
    isSIBlurred(val) {
      this.isSIBlurredFlag = val;
    },
    regoErrorMessages(val) {
      this.regoErrorMessagesText = val;
    },
    siErrorMessages(val) {
      this.siErrorMessagesText = val;
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .el-dialog__body{
    display: inline-block;
    // flex-direction: column;
    // align-items: center;
  }
.member-selection {
  border: 1px solid #dcdcdc;
  border-radius: 4px;
  padding: 10px;
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 20px;
}

.member-item {
  border-bottom: 1px solid #dcdcdc;
  padding: 10px 0;
}

.member-item:last-child {
  border-bottom: none;
}
.container {
  background-color: #f5f5f5;
  padding: 20px;
}

.confirmation-wrapper {
  background: #fff;
  border-radius: 12px;
  margin-bottom: 20px;
  padding: 20px;
  // box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: grid;
  gap: 20px;
  .section-header {
    font-size: 1.5em;
    color: #303133;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid var(--highlight-color);
  }
}

.registration-card {
  margin-bottom: 30px;
  padding: 20px;
  background-color: #fff;
  border-radius: 8px;
  // box-shadow: 0 2px 12px 0 rgba(0,0,0,.08);
  border-bottom: 1px solid #EBEEF5;

  &:last-child {
    margin-bottom: 0;
  }

  .event-name {
    display: flex;
    align-items: center;
    font-size: 1.5em;
    margin-bottom: 15px;
    color: #303133;

    .el-tag {
      margin-right: 10px;
    }
  }

  .event-details {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 15px;

    .detail-item {
      flex: 1 1 50%;
      display: flex;
      align-items: center;
      margin-bottom: 10px;

      i {
        margin-right: 8px;
        color: var(--highlight-color);
      }
    }
  }

  .calendar-link-wrapper {
    margin-bottom: 15px;
  }

  .calendar-link {
    color: #409EFF;
    cursor: pointer;
    display: inline-flex;
    align-items: center;

    i {
      margin-right: 5px;
    }
  }

  .pricing, .subtotal {
    display: flex;
    justify-content: space-between;
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #EBEEF5;

    .price-label, .subtotal-label {
      color: #606266;
    }

    .price-value, .subtotal-value {
      font-weight: bold;
      color: #333;
    }
  }

  .family-members, .saleable-items, .team-products, .team-code-section {
    margin-top: 20px;

    h3 {
      margin-bottom: 15px;
      color: #303133;
    }
  }

  .family-members-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }

  .family-member {
    background-color: #f5f7fa;
    border-radius: 8px;
    padding: 15px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    .member-info {
      flex: 1;
    }

    .member-name {
      font-weight: bold;
      margin-bottom: 5px;
      color: #303133;
    }

    .member-details {
      display: flex;
      flex-direction: column;
      font-size: 0.9em;
      color: #606266;

      span {
        margin-bottom: 3px;
      }
    }

    .el-tag {
      margin-left: 10px;
      flex-shrink: 0;
    }
  }

  .team-product {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #f5f7fa;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 10px;

    .team-product-details {
      display: flex;
      flex-direction: column;
    }

    .team-product-tags {
      display: flex;
      gap: 5px;
      margin-bottom: 10px;

      .el-tag {
        margin-right: 5px;
      }
    }

    .team-product-name {
      font-weight: bold;
      margin-bottom: 10px;
    }

    .team-product-price {
      color: #606266;
    }

    .team-product-actions {
      display: flex;
      gap: 5px;
    }
  }
}

.saleable-item {
  background-color: #fff;
  // border-radius: 8px;
  // box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid #EBEEF5;
  padding: 20px;
  // margin-bottom: 20px;
  &:last-child {
    margin-bottom: 0;
    border-bottom: none;
  }

  .item-content {
    display: flex;
    align-items: center;
  }

  .item-image {
    flex: 0 0 100px;
    height: 100px;
    margin-right: 20px;
    // border-radius: 12px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 12px;
    }
  }

  .item-details {
    flex: 1;

    .item-name {
      font-size: 1.2em;
      margin: 0 0 10px 0;
      color: #303133;
    }

    .item-info {
      display: flex;
      flex-direction: column;
      font-size: 0.9em;
      color: #606266;

      .quantity, .price {
        margin-bottom: 5px;
      }
    }
  }

  .item-total {
    text-align: right;
    min-width: 120px;

    .total-label {
      display: block;
      font-size: 0.9em;
      color: #606266;
      margin-bottom: 5px;
    }

    .total-value {
      font-size: 1.2em;
      font-weight: bold;
      color: #333;
    }
  }
}

.total-amount {
  margin-top: 20px;

  .registration-card {
    background-color: #f0f8ff;
  }

  .subtotal {
    font-size: 1.3em;
    color: #333;

    .subtotal-label, .subtotal-value {
      font-weight: bold;
    }
    .subtotal-label {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .subtotal-label-small {
    font-size: 0.8em;
    color: #909399;
    margin-top: 2px;
  }

    .subtotal-value {
      color: #333;
    }
  }
}

.blurred-text {
  filter: blur(5px);
}

.blurred {
  position: relative;
  opacity: 0.8;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.15);
    border-radius: 10px;
    z-index: 1;
    filter: blur(2px);
  }

  &::after {
    content: 'Processing...';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    z-index: 2;
  }
}

.el-divider {
  margin: 15px 0;

  &__text {
    font-weight: bold;
    color: #606266;
  }
}

@media (max-width: 768px) {
  .container {
    padding: 10px;
  }

  .confirmation-wrapper, .registration-card {
    padding: 15px;
    .section-header {
      font-size: 1.3em;
      margin-bottom: 15px;
    }
  }

  .registration-card {
    .event-details {
      .detail-item {
        flex: 1 1 100%;
      }
    }

    .team-product {
      flex-direction: column;
      align-items: flex-start;

      .team-product-actions {
        margin-top: 10px;
        align-self: flex-end;
      }
    }

    .family-members-grid {
      grid-template-columns: 1fr;
    }

    .family-member {
      flex-direction: row; // Keep horizontal layout
      align-items: center; // Center align items vertically

      .member-info {
        flex: 1;
        min-width: 0; // Allow text to wrap
      }

      .member-name {
        font-size: 0.9em; // Slightly reduce font size for mobile
      }

      .member-details {
        font-size: 0.8em; // Further reduce font size for details

        span {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .el-tag {
        margin-left: 10px;
        font-size: 0.8em; // Reduce tag font size for mobile
      }
    }
  }

  .saleable-item {
    padding: 15px;

    .item-image {
      flex: 0 0 80px;
      height: 80px;
      margin-right: 15px;
    }

    .item-details {
      .item-name {
        font-size: 1em;
      }

      .item-info {
        font-size: 0.8em;
      }
    }

    .item-total {
      min-width: 100px;

      .total-label {
        font-size: 0.8em;
      }

      .total-value {
        font-size: 1em;
      }
    }
  }

  .total-amount {
    .subtotal {
      // flex-direction: column;
      align-items: flex-start;

      .subtotal-value {
        margin-top: 5px;
      }
    }
  }
}

.team-code-compact {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  margin-top: 10px;

  .code-value {
    font-family: 'Courier New', monospace;
    font-size: 1em;
    font-weight: bold;
    color: #409eff;
    background-color: #fff;
    padding: 4px 8px;
    border-radius: 3px;
    border: 1px solid #c6e2ff;
  }
}

.team-code-note {
  margin-top: 5px;
  color: #606266;

  i {
    margin-right: 4px;
    color: #409eff;
  }
}

.team-code-option {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;

  .el-checkbox {
    margin-bottom: 10px;
  }

  .team-code-info {
    .el-alert {
      border-radius: 6px;
    }
  }
}
</style>
